/**
 * Repair "mojibake": UTF-8 text that was wrongly decoded as Windows-1252,
 * e.g. "Las PiÃ±as" → "Las Piñas", "PEÃ‘AFRANCIA" → "PEÑAFRANCIA".
 *
 * The PSGC address API (psgc.cloud) returns names in this broken form.
 * Each character is turned back into the byte it came from and the bytes
 * are decoded again as UTF-8. Text that isn't broken is returned unchanged.
 */

// Windows-1252 maps bytes 0x80–0x9F to these characters (others are 1:1 with Latin-1).
const CP1252_TO_BYTE = {
  "€": 0x80,
  "‚": 0x82,
  ƒ: 0x83,
  "„": 0x84,
  "…": 0x85,
  "†": 0x86,
  "‡": 0x87,
  ˆ: 0x88,
  "‰": 0x89,
  Š: 0x8a,
  "‹": 0x8b,
  Œ: 0x8c,
  Ž: 0x8e,
  "‘": 0x91,
  "’": 0x92,
  "“": 0x93,
  "”": 0x94,
  "•": 0x95,
  "–": 0x96,
  "—": 0x97,
  "˜": 0x98,
  "™": 0x99,
  š: 0x9a,
  "›": 0x9b,
  œ: 0x9c,
  ž: 0x9e,
  Ÿ: 0x9f,
};

// Garbled UTF-8 always starts with Ã (0xC3) or Â (0xC2) for Latin letters.
const LOOKS_GARBLED = /[ÂÃ][\u0080-¿‘-™Œ-Ÿˆ˜€…‰‹›Š-ž]/;

const utf8Decoder =
  typeof TextDecoder !== "undefined"
    ? new TextDecoder("utf-8", { fatal: true })
    : null;

export const fixMojibake = (value) => {
  if (typeof value !== "string" || !utf8Decoder || !LOOKS_GARBLED.test(value))
    return value;
  const bytes = [];
  for (const ch of value) {
    const code = ch.charCodeAt(0);
    if (code <= 0xff) bytes.push(code);
    else if (CP1252_TO_BYTE[ch] !== undefined) bytes.push(CP1252_TO_BYTE[ch]);
    else return value; // not reversible → leave as-is
  }
  try {
    return utf8Decoder.decode(new Uint8Array(bytes)).normalize("NFC");
  } catch {
    return value; // bytes weren't valid UTF-8 → it wasn't mojibake
  }
};

/** Apply fixMojibake to the given string fields of an object. */
export const fixMojibakeFields = (obj, fields) => {
  if (!obj) return obj;
  const out = { ...obj };
  fields.forEach((f) => {
    out[f] = fixMojibake(out[f]);
  });
  return out;
};

/** "First Last" from any object with firstName/lastName; "—" when missing. */
export const employeeName = (e) =>
  e ? `${e.firstName || ""} ${e.lastName || ""}`.trim() || "—" : "—";

// Name particles that stay lowercase (not as the first word) and suffixes that stay uppercase
const PARTICLES = new Set([
  "de",
  "del",
  "dela",
  "della",
  "delos",
  "los",
  "la",
  "las",
  "y",
  "van",
  "von",
  "da",
  "di",
  "dos",
]);
const ROMAN = /^(ii|iii|iv|v|vi|vii|viii|ix|x)$/i;

/**
 * Display-only proper case: "ADEMIR LAGUA" → "Ademir Lagua", "juan DELA cruz iii"
 * → "Juan dela Cruz III". Keeps hyphen / apostrophe parts ("Ana-Marie", "O'Neil").
 * Never use it on data that is saved.
 */
export const properName = (name) => {
  if (!name) return name || "";
  const word = (w, i) => {
    const lower = w.toLowerCase();
    if (ROMAN.test(lower)) return lower.toUpperCase();
    if (i > 0 && PARTICLES.has(lower)) return lower;
    if (/^jr\.?$|^sr\.?$/i.test(lower))
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    return lower.replace(
      /(^|[-'’])(\p{L})/gu,
      (m, sep, ch) => sep + ch.toUpperCase(),
    );
  };
  return String(name).trim().split(/\s+/).map(word).join(" ");
};

const AVATAR_COLORS = [
  "#2563eb",
  "#7c3aed",
  "#db2777",
  "#dc2626",
  "#ea580c",
  "#ca8a04",
  "#16a34a",
  "#0891b2",
];
/** A stable avatar colour per person (hash of their id). */
export const avatarColor = (id = "") => {
  let h = 0;
  for (const ch of String(id)) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
